import React, { useState } from 'react';
import { Clock, Pin, MapPin, Star, Save, Globe, Check } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения мировых часов в виде сетки
 */
const WorldClockGrid = ({ 
  worldClocks, 
  selectedTimeZones, 
  setSelectedTimeZones,
  saveTimeZone 
}) => {
  const [saveName, setSaveName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  if (!worldClocks || worldClocks.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8 text-center">
        <Clock className="w-10 h-10 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
        <p className="text-gray-600 dark:text-gray-400">Выберите параметры и нажмите "Рассчитать время" для отображения мировых часов.</p>
      </div>
    );
  }

  // Фильтрация российских часовых поясов
  const russianTimeZones = worldClocks.filter(city => 
    city.timezone.startsWith('Europe/') || city.timezone.startsWith('Asia/')
  );
  
  // Фильтрация мировых часовых поясов
  const internationalTimeZones = worldClocks.filter(city => 
    !city.timezone.startsWith('Europe/') && !city.timezone.startsWith('Asia/')
  );

  // Обработчик выбора часового пояса для сохранения
  const handleToggleSelect = (cityId) => {
    setSelectedTimeZones(prev => {
      if (prev.includes(cityId)) {
        return prev.filter(id => id !== cityId);
      } else {
        return [...prev, cityId];
      }
    });
  };

  // Обработчик сохранения набора часовых поясов
  const handleSaveTimeZones = () => {
    if (showSaveDialog) {
      const saved = saveTimeZone(saveName || 'Мои часовые пояса');
      if (saved) {
        alert(`Набор часовых поясов "${saved.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Функция для форматирования даты на русском языке
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    // Преобразуем формат DD.MM.YYYY
    const parts = dateStr.split('.');
    if (parts.length !== 3) return dateStr;
    
    // Месяцы на русском (краткая форма)
    const months = [
      'янв', 'фев', 'мар', 'апр', 'май', 'июн', 
      'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'
    ];
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    
    return `${day} ${months[month]} ${year}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <Globe className="w-5 h-5 mr-2 text-purple-500" />
          Мировые часы
        </h2>
        <div className="flex space-x-2">
          <Button
            size="sm"
            onClick={handleSaveTimeZones}
            icon={<Save className="w-4 h-4" />}
            variant={selectedTimeZones.length > 0 ? "primary" : "outline"}
            disabled={selectedTimeZones.length === 0 && !showSaveDialog}
          >
            {showSaveDialog ? "Сохранить выбранные" : "Сохранить набор"}
          </Button>
        </div>
      </div>

      {/* Диалог сохранения набора часовых поясов */}
      {showSaveDialog && (
        <div className="mb-4 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg">
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Введите название для сохранения"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              className="form-input flex-grow"
              autoFocus
            />
            <Button
              type="button"
              onClick={handleSaveTimeZones}
              primary
              size="sm"
            >
              Сохранить
            </Button>
          </div>
          <p className="mt-2 text-xs text-purple-600 dark:text-purple-400">
            Выбрано часовых поясов: {selectedTimeZones.length}
          </p>
        </div>
      )}

      {/* Часы для России */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <MapPin className="w-4 h-4 mr-2 text-red-500" />
          Часовые пояса России
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {russianTimeZones.map((city) => (
            <div 
              key={city.id} 
              className={`p-4 rounded-lg border transition-colors ${
                selectedTimeZones.includes(city.id) 
                  ? 'bg-purple-50 border-purple-300 dark:bg-purple-900/30 dark:border-purple-700' 
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">{city.name}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    UTC{city.utcOffset}
                  </p>
                </div>
                <button 
                  type="button"
                  onClick={() => handleToggleSelect(city.id)}
                  className={`p-1 rounded-full transition-colors ${
                    selectedTimeZones.includes(city.id) 
                      ? 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/50' 
                      : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
                  }`}
                  aria-label={selectedTimeZones.includes(city.id) ? "Убрать из избранного" : "Добавить в избранное"}
                >
                  {selectedTimeZones.includes(city.id) ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Star className="w-5 h-5" />
                  )}
                </button>
              </div>
              
              <div className="mt-3">
                <div className="clock-display font-mono text-purple-700 dark:text-purple-300">
                  {city.currentTime}
                </div>
                {city.currentDate && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                    {formatDate(city.currentDate)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Часы для мира */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <Globe className="w-4 h-4 mr-2 text-blue-500" />
          Международные часовые пояса
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {internationalTimeZones.map((city) => (
            <div 
              key={city.id} 
              className={`p-4 rounded-lg border transition-colors ${
                selectedTimeZones.includes(city.id) 
                  ? 'bg-purple-50 border-purple-300 dark:bg-purple-900/30 dark:border-purple-700' 
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">{city.name}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    UTC{city.utcOffset}
                  </p>
                </div>
                <button 
                  type="button"
                  onClick={() => handleToggleSelect(city.id)}
                  className={`p-1 rounded-full transition-colors ${
                    selectedTimeZones.includes(city.id) 
                      ? 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/50' 
                      : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
                  }`}
                  aria-label={selectedTimeZones.includes(city.id) ? "Убрать из избранного" : "Добавить в избранное"}
                >
                  {selectedTimeZones.includes(city.id) ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Star className="w-5 h-5" />
                  )}
                </button>
              </div>
              
              <div className="mt-3">
                <div className="clock-display font-mono text-blue-700 dark:text-blue-300">
                  {city.currentTime}
                </div>
                {city.currentDate && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                    {formatDate(city.currentDate)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Нижняя информационная панель */}
      <div className="mt-6 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-sm text-gray-600 dark:text-gray-400">
        <p className="flex items-center">
          <Clock className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
          Все часы синхронизированы с вашим локальным временем. Обновлено: {new Date().toLocaleTimeString('ru-RU')}
        </p>
      </div>
    </div>
  );
};

export default WorldClockGrid;